import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Printer, Plus, Trash, Check } from "@phosphor-icons/react";

const n0 = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const n2 = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().slice(0, 10);

const lineOperatorTotals = (rows) => {
  const by = {};
  rows.forEach((r) => {
    const ops = r.operators || [];
    ops.forEach((o) => {
      const pct = Number(o.percent || 100 / ops.length);
      const d = by[o.employee_id] || (by[o.employee_id] = { employee_id: o.employee_id, name: o.name, weight: 0, pieces: 0, count: 0 });
      d.weight += Number(r.weight || 0) * pct / 100;
      d.pieces += Number(r.pieces || 0) * pct / 100;
      d.count += 1;
    });
  });
  return Object.values(by).sort((a, b) => b.weight - a.weight);
};

export default function WeightsSplit() {
  const [params] = useSearchParams();
  const [day, setDay] = useState(params.get("data") || todayStr());
  const [line, setLine] = useState(params.get("linea") || "SUINO");
  const [crew, setCrew] = useState({ lines: {}, split_lines: ["SUINO", "BOVINO"] });
  const [members, setMembers] = useState([]);
  const [rows, setRows] = useState([]);
  const [operators, setOperators] = useState([]);
  const liberi = useMemo(() => operators.filter(
    (o) => !members.some((m) => m.employee_id === o.employee_id)), [operators, members]);
  const [selected, setSelected] = useState([]);
  const [rowEdit, setRowEdit] = useState(null);

  const load = useCallback(async () => {
    try {
      const c = await api.get("/weights/crew", { params: { day } });
      setCrew(c.data);
      setMembers((c.data.lines[line]?.members || []).map((m) => ({ ...m })));
      const d = await api.get("/weights/day", { params: { day } });
      setRows((d.data.rows || []).filter((r) => r.line === line));
      setSelected([]);
    } catch (e) { toast.error(errMsg(e)); }
  }, [day, line]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/weights/operators").then((r) => setOperators(r.data)).catch(() => {}); }, []);

  const total = Math.round(members.reduce((a, m) => a + Number(m.percent || 0), 0) * 100) / 100;
  const lineTotals = lineOperatorTotals(rows);

  // riparto equo a numeri interi con somma esatta 100 (3 cottimisti -> 34/33/33)
  const ripartisci = (lista) => {
    const n = lista.length;
    if (!n) return [];
    const base = Math.floor(100 / n);
    const resto = 100 - base * n;
    return lista.map((m, i) => ({ ...m, percent: base + (i < resto ? 1 : 0) }));
  };

  const rowTotal = rowEdit
    ? Math.round(rowEdit.members.reduce((a, m) => a + Number(m.percent || 0), 0) * 100) / 100
    : 0;

  const addMember = (employee_id) => {
    if (!employee_id || members.some((m) => m.employee_id === employee_id)) return;
    const o = operators.find((x) => x.employee_id === employee_id);
    const next = [...members, { employee_id, name: o?.name, percent: 0 }];
    const even = Math.round((100 / next.length) * 100) / 100;
    setMembers(next.map((m, i) => ({ ...m, percent: i === next.length - 1 ? Math.round((100 - even * (next.length - 1)) * 100) / 100 : even })));
  };

  const saveCrew = async (apply, entryIds) => {
    const body = { date: day, line, members: members.map((m) => ({ employee_id: m.employee_id, percent: Number(m.percent || 0) })),
      apply_existing: !!apply, entry_ids: entryIds || [] };
    // risposta immediata: il salvataggio e la rigenerazione dei PDF proseguono in background
    toast.success(apply
      ? `Divisione applicata a ${entryIds?.length ? entryIds.length + " lavorazioni" : "tutte le lavorazioni " + line}`
      : "Squadra salvata");
    try {
      const r = await api.put("/weights/crew", body);
      setCrew(r.data);
      load();
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const saveRowSplit = async () => {
    if (rowTotal !== 100) { toast.error("Il totale delle percentuali deve essere 100%"); return; }
    const id = rowEdit.id;
    const membri = rowEdit.members.map((m) => ({ employee_id: m.employee_id, percent: Number(m.percent || 0) }));
    setRowEdit(null);
    toast.success("Lavorazione aggiornata");
    try {
      await api.put(`/weights/entries/${id}/split`, { members: membri });
      load();
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const printAllLines = async () => {
    // apre subito 4 schede (gesto utente) e poi ci carica dentro i PDF
    const linee = ["BOVINO", "SUINO", "CRT", "RILAVORAZIONE"];
    const schede = linee.map(() => window.open("", "_blank"));
    let done = 0;
    for (let i = 0; i < linee.length; i += 1) {
      try {
        const r = await api.get(`/weights/export/linea-pdf?day=${day}&line=${linee[i]}`,
          { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        const w = schede[i] || window.open("", "_blank");
        if (w) {
          w.document.write(
            `<!doctype html><title>${day} - ${linee[i]}</title>`
            + `<style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style>`
            + `<iframe src="${url}"></iframe>`);
          w.document.close();
        }
        done += 1;
      } catch (e) {
        console.error(`PDF linea ${linee[i]} non disponibile`, e);
        schede[i]?.close();
      }
    }
    if (done) toast.success(`${done} PDF aperti in altre schede`);
    else toast.error(`Nessuna pesata registrata il ${day}`);
  };

  return (
    <div className="space-y-3" data-testid="weights-split-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Divisione Pesi</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Solo linee SUINO e BOVINO: indica chi lavora a cottimo e in che percentuale, poi controlla e
            correggi come sono state divise le singole lavorazioni.
          </p>
        </div>
        <div className="mobile-stack flex flex-wrap gap-2 items-center max-w-full">
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
            className="h-9 w-[160px] rounded-sm" data-testid="split-day" />
          <Select value={line} onValueChange={setLine}>
            <SelectTrigger className="h-9 w-[150px] rounded-sm" data-testid="split-line"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(crew.split_lines || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="rounded-sm font-semibold" data-testid="split-print-lines"
            onClick={printAllLines}>
            <Printer size={14} className="mr-1.5" />
            <span className="hidden lg:inline">GENERA I 4 PDF (BOVINO/SUINO/CARTONATO/RILAVORAZIONI)</span>
            <span className="lg:hidden">GENERA I 4 PDF</span>
          </Button>
        </div>
      </div>

      {/* squadra della linea */}
      <div className="rounded-sm border border-border bg-card p-3 space-y-3" data-testid="split-crew">
        <div className="mobile-stack flex flex-wrap items-center gap-2">
          <h2 className="font-head font-semibold text-base md:text-lg">Cottimisti sulla linea {line}</h2>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm tabular-nums ${total === 100 ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}
            data-testid="split-total-percent">totale {total}%</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Select value="" onValueChange={addMember}>
              <SelectTrigger className="h-8 w-[220px] rounded-sm" data-testid="split-add-member">
                <SelectValue placeholder="Aggiungi cottimista" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {liberi.map((o) => <SelectItem key={o.employee_id} value={o.employee_id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 rounded-sm" data-testid="split-save-crew"
              onClick={() => saveCrew(false)}>Salva squadra</Button>
            <Button size="sm" className="h-8 rounded-sm font-semibold" data-testid="split-apply-all"
              onClick={() => saveCrew(true)}>Applica a tutte le lavorazioni {line}</Button>
            <Button size="sm" className="h-8 rounded-sm font-semibold" data-testid="split-apply-selected"
              disabled={!selected.length} onClick={() => saveCrew(true, selected)}>
              Applica alle {selected.length} selezionate
            </Button>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {members.map((m, i) => (
            <div key={m.employee_id} className="flex items-center gap-2 rounded-sm border border-border p-2"
              data-testid={`split-member-${m.employee_id}`}>
              <span className="text-sm truncate flex-1">{m.name}</span>
              <Input value={m.percent} onChange={(e) => {
                const next = [...members];
                next[i] = { ...m, percent: e.target.value };
                setMembers(next);
              }} className="h-8 w-20 rounded-sm text-right tabular-nums" data-testid={`split-percent-${m.employee_id}`} />
              <span className="text-xs text-muted-foreground">%</span>
              <button className="text-muted-foreground hover:text-rose-500" data-testid={`split-remove-${m.employee_id}`}
                onClick={() => setMembers(members.filter((x) => x.employee_id !== m.employee_id))}>
                <Trash size={14} />
              </button>
            </div>
          ))}
          {members.length === 0 && (
            <div className="text-sm text-muted-foreground" data-testid="split-no-members">
              Nessun cottimista impostato per la linea {line} in questa giornata
            </div>
          )}
        </div>
      </div>

      {/* quote del giorno sulla linea selezionata */}
      {lineTotals.length > 0 && (
        <div className="rounded-sm border border-border bg-card overflow-x-auto" data-testid="split-day-totals">
          <table className="w-full text-sm">
            <thead className="bg-secondary"><tr>
              {[`Cottimista (linea ${line})`, "Lavorazioni", "Pezzi", "Kg assegnati"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {lineTotals.map((o) => (
                <tr key={o.employee_id} className="border-t border-border" data-testid={`split-total-${o.employee_id}`}>
                  <td className="px-3 py-1.5">{o.name}</td>
                  <td className="px-3 py-1.5 tabular-nums">{o.count}</td>
                  <td className="px-3 py-1.5 tabular-nums">{n0(o.pieces)}</td>
                  <td className="px-3 py-1.5 tabular-nums font-semibold">{n2(o.weight)}</td>
                </tr>
              ))}
              <tr className="border-t border-border bg-secondary/60 font-semibold" data-testid="split-total-line">
                <td className="px-3 py-1.5">TOTALE LINEA {line}</td>
                <td className="px-3 py-1.5 tabular-nums">{rows.length}</td>
                <td className="px-3 py-1.5 tabular-nums">{n0(lineTotals.reduce((a, o) => a + o.pieces, 0))}</td>
                <td className="px-3 py-1.5 tabular-nums">{n2(lineTotals.reduce((a, o) => a + o.weight, 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* lavorazioni della linea */}
      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm tbl-sticky" data-testid="split-rows-table">
          <thead className="bg-secondary"><tr>
            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" />
            <th className="px-2 py-2 w-10">
              <input type="checkbox" data-testid="split-select-all"
                checked={selected.length > 0 && selected.length === rows.length}
                onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.id) : [])} />
            </th>
            {["Sigla", "Lavorazione", "Cliente", "Pezzi", "Peso", "Divisione applicata"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`split-row-${r.id}`}>
                <td className="px-3 py-1.5 text-left">
                  <button type="button" className="text-xs font-semibold hover:underline px-1 py-1" data-testid={`split-edit-${r.id}`}
                    onClick={() => setRowEdit({
                      ...r,
                      members: (r.operators || []).length
                        ? r.operators.map((o) => ({ employee_id: o.employee_id, name: o.name, percent: o.percent || Math.round(100 / r.operators.length * 100) / 100 }))
                        : members.map((m) => ({ ...m })),
                    })}>MODIFICA</button>
                </td>
                <td className="px-2 py-1.5">
                  <input type="checkbox" checked={selected.includes(r.id)} data-testid={`split-check-${r.id}`}
                    onChange={(e) => setSelected(e.target.checked ? [...selected, r.id] : selected.filter((x) => x !== r.id))} />
                </td>
                <td className="px-3 py-1.5 font-semibold">{r.code}</td>
                <td className="px-3 py-1.5">{r.description}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.client}</td>
                <td className="px-3 py-1.5 tabular-nums">{n0(r.pieces)}</td>
                <td className="px-3 py-1.5 tabular-nums">{n2(r.weight)}</td>
                <td className="px-3 py-1.5 text-xs">
                  {(r.operators || []).length
                    ? (r.operators || []).map((o) => (
                      <span key={o.employee_id} className="inline-block mr-2">
                        {o.name} <span className="font-semibold tabular-nums">{Math.round((o.percent || 100 / r.operators.length) * 10) / 10}%</span>
                        <span className="text-muted-foreground"> ({n2(r.weight * (o.percent || 100 / r.operators.length) / 100)} kg)</span>
                      </span>
                    ))
                    : <span className="text-amber-600 font-semibold">NON DIVISA</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground" data-testid="split-empty">
                Nessuna lavorazione {line} in questa giornata
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {rowEdit && (
        <Dialog open onOpenChange={(o) => !o && setRowEdit(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="split-dialog">
            <DialogHeader>
              <DialogTitle className="font-head text-base">{rowEdit.code} — {rowEdit.description}</DialogTitle>
              <DialogDescription className="text-xs">
                {n2(rowEdit.weight)} kg · {n0(rowEdit.pieces)} pezzi
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {rowEdit.members.map((m, i) => (
                <div key={m.employee_id} className="flex items-center gap-2">
                  <span className="text-sm flex-1 truncate">{m.name}</span>
                  <Input value={m.percent} onChange={(e) => {
                    const next = [...rowEdit.members];
                    next[i] = { ...m, percent: e.target.value };
                    setRowEdit({ ...rowEdit, members: next });
                  }} className="h-8 w-20 rounded-sm text-right tabular-nums" data-testid={`split-row-percent-${m.employee_id}`} />
                  <span className="text-xs text-muted-foreground">%</span>
                  <button className="text-muted-foreground hover:text-rose-500"
                    data-testid={`split-row-remove-${m.employee_id}`}
                    onClick={() => setRowEdit({
                      ...rowEdit,
                      members: ripartisci(rowEdit.members.filter((x) => x.employee_id !== m.employee_id)),
                    })}>
                    <Trash size={14} />
                  </button>
                </div>
              ))}
              <Select value="" onValueChange={(v) => {
                const o = operators.find((x) => x.employee_id === v);
                if (o && !rowEdit.members.some((m) => m.employee_id === v)) {
                  setRowEdit({
                    ...rowEdit,
                    members: ripartisci([...rowEdit.members, { employee_id: v, name: o.name, percent: 0 }]),
                  });
                }
              }}>
                <SelectTrigger className="h-8 rounded-sm" data-testid="split-row-add">
                  <SelectValue placeholder="Aggiungi cottimista a questa lavorazione" />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  {operators.map((o) => <SelectItem key={o.employee_id} value={o.employee_id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className={`text-xs ${rowTotal === 100 ? "text-emerald-600" : "text-rose-600 font-semibold"}`}
                data-testid="split-row-total">
                Totale: <span className="font-semibold tabular-nums">{rowTotal}%</span>
                {rowTotal === 100 ? " — ripartizione corretta" : " — deve essere 100%"}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" data-testid="split-row-cancel"
                onClick={() => setRowEdit(null)}>Annulla</Button>
              <Button size="sm" className="rounded-sm font-semibold" data-testid="split-row-save"
                disabled={rowTotal !== 100} onClick={saveRowSplit}>
                <Check size={14} className="mr-1.5" /> Salva divisione
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <p className="text-xs text-muted-foreground">
        Suggerimento: seleziona le righe da correggere e usa "Applica alle selezionate" per cambiare in blocco
        solo alcune lavorazioni. <Plus size={12} className="inline" /> Le nuove pesate ereditano la squadra della linea.
      </p>
    </div>
  );
}
